import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { href } from "../app/router.js";
import { IconState } from "../components/Icons.jsx";
import {
  HELP_GUIDES,
  HELP_TOPICS,
  NOTE_LABEL,
  SURFACE_LABEL,
  findGuide,
  type HelpBlock,
  type HelpGuide,
} from "../help/guides.js";
import { searchGuides, type GuideMatch } from "../help/search.js";

/**
 * The Help centre: short, practical guides to Scannix's own features — the
 * iPhone app and this portal — written from what the code does today.
 *
 * `HelpCentre` is the list with its search; `HelpGuidePage` is one guide.
 * Every guide is rendered by the one `GuideView`, from the structured data in
 * `help/guides.ts`, so they cannot drift apart in shape.
 *
 * The search is kept in the address (`#/help?q=…`) with `replaceState`, so
 * Back from a guide returns to the same results rather than an empty box. The
 * router ignores everything after `?`, so the route is still `help`.
 */

function queryFromHash(): string {
  try {
    const hash = globalThis.location?.hash ?? "";
    const at = hash.indexOf("?");
    if (at < 0) return "";
    return new URLSearchParams(hash.slice(at + 1)).get("q") ?? "";
  } catch {
    return "";
  }
}

function rememberQuery(query: string): void {
  try {
    const trimmed = query.trim();
    const next = trimmed === "" ? href.help() : `${href.help()}?q=${encodeURIComponent(trimmed)}`;
    globalThis.history?.replaceState(globalThis.history.state, "", next);
  } catch {
    // A sandboxed frame can refuse history writes; the search still works.
  }
}

export function HelpCentre(): ReactNode {
  const [query, setQuery] = useState(queryFromHash);
  const searchId = useId();
  const matches = useMemo(() => searchGuides(HELP_GUIDES, query), [query]);
  const searching = query.trim() !== "";

  useEffect(() => {
    rememberQuery(query);
  }, [query]);

  return (
    <>
      <div className="page-header">
        <h1>Help centre</h1>
        <p className="lede">
          Short guides to Scannix — the iPhone app and this portal — written from what they do
          today. Where something needs a real iPhone, or is general advice rather than a Scannix
          feature, the guide says so.
        </p>
      </div>

      <div className="help-search" role="search">
        <label htmlFor={searchId} className="help-search__label">
          Search the guides
        </label>
        <input
          id={searchId}
          type="search"
          className="help-search__input"
          value={query}
          placeholder="e.g. send failed, radiator, laser, stairs"
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
        />
        <p className="help-search__count small muted" role="status" aria-live="polite">
          {searching
            ? matches.length === 0
              ? "No guide matches."
              : `${matches.length} ${matches.length === 1 ? "guide matches" : "guides match"}.`
            : `${HELP_GUIDES.length} guides.`}
        </p>
      </div>

      {searching ? (
        matches.length === 0 ? (
          <div className="state state--empty help-empty">
            <span className="state__glyph">
              <IconState kind="empty" />
            </span>
            <p className="state__title">Nothing matches “{query.trim()}”</p>
            <p className="state__body">
              Try a shorter word, or a word from the screen — a button&apos;s label, say.
            </p>
            <button type="button" className="secondary" onClick={() => setQuery("")}>
              Clear the search
            </button>
          </div>
        ) : (
          <GuideList matches={matches} label="Search results" />
        )
      ) : (
        HELP_TOPICS.map((topic) => {
          const inTopic = matches.filter((m) => m.guide.topic === topic.id);
          if (inTopic.length === 0) return null;
          return (
            <section key={topic.id} className="help-topic" aria-labelledby={`help-topic-${topic.id}`}>
              <h2 id={`help-topic-${topic.id}`} className="help-topic__title">
                {topic.title}
              </h2>
              <GuideList matches={inTopic} label={topic.title} />
            </section>
          );
        })
      )}
    </>
  );
}

function GuideList({ matches, label }: { readonly matches: readonly GuideMatch[]; readonly label: string }): ReactNode {
  return (
    <ul className="help-list" aria-label={label}>
      {matches.map(({ guide, section }) => (
        <li key={guide.id} className="help-list__item">
          <a className="help-card" href={href.helpGuide(guide.id)}>
            <span className="help-card__title">{guide.title}</span>
            <span className="help-card__summary">{guide.summary}</span>
            <span className="help-card__meta">
              <AppliesTo guide={guide} />
              {section ? <span className="help-card__where small muted">Found in: {section}</span> : null}
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function AppliesTo({ guide }: { readonly guide: HelpGuide }): ReactNode {
  return (
    <span className="help-applies">
      <span className="visually-hidden">Applies to: </span>
      {guide.appliesTo.map((surface) => (
        <span key={surface} className={`pill help-applies__pill help-applies__pill--${surface}`}>
          {SURFACE_LABEL[surface]}
        </span>
      ))}
    </span>
  );
}

export function HelpGuidePage({ id }: { readonly id: string }): ReactNode {
  const guide = findGuide(id);
  if (!guide) {
    return (
      <div className="state state--empty">
        <span className="state__glyph">
          <IconState kind="empty" />
        </span>
        <p className="state__title">No such guide</p>
        <p className="state__body">
          There is no guide at <span className="mono">{id}</span>. It may have been renamed.
        </p>
        <a className="cta" href={href.help()}>
          Back to the Help centre
        </a>
      </div>
    );
  }
  return <GuideView guide={guide} />;
}

/** One guide, however it is reached. */
export function GuideView({ guide }: { readonly guide: HelpGuide }): ReactNode {
  const related = guide.related.map(findGuide).filter((g): g is HelpGuide => g !== undefined);

  // The router changes only the hash, so the page would otherwise open where
  // the last one was scrolled to — a related guide, picked at the foot of one
  // guide, would open at the foot of the next.
  useEffect(() => {
    const page = globalThis.document?.scrollingElement;
    if (page) page.scrollTop = 0;
  }, [guide.id]);
  return (
    <article className="help-guide" aria-labelledby="help-guide-title">
      <div className="page-header">
        <p className="small">
          <a href={href.help()}>Help centre</a>
        </p>
        <h1 id="help-guide-title">{guide.title}</h1>
        <p className="lede">{guide.summary}</p>
      </div>
      <p className="help-guide__applies">
        <AppliesTo guide={guide} />
      </p>

      {guide.sections.map((section) => (
        <section key={section.heading} className="help-section" aria-label={section.heading}>
          <h2 className="help-section__title">{section.heading}</h2>
          {section.blocks.map((block, index) => (
            <Block key={index} block={block} />
          ))}
        </section>
      ))}

      {related.length > 0 ? (
        <nav className="help-related" aria-label="Related guides">
          <h2 className="help-related__title">Related guides</h2>
          <ul className="help-related__list">
            {related.map((other) => (
              <li key={other.id}>
                <a href={href.helpGuide(other.id)}>{other.title}</a>
                <span className="small muted"> — {other.summary}</span>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </article>
  );
}

function Block({ block }: { readonly block: HelpBlock }): ReactNode {
  switch (block.kind) {
    case "p":
      return (
        <p className="help-p">
          <Inline text={block.text} />
        </p>
      );
    case "steps":
      return (
        <ol className="help-steps">
          {block.items.map((item, index) => (
            <li key={index}>
              <Inline text={item} />
            </li>
          ))}
        </ol>
      );
    case "list":
      return (
        <ul className="help-bullets">
          {block.items.map((item, index) => (
            <li key={index}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );
    case "note":
      return (
        <aside className={`help-note help-note--${block.tone}`} role="note">
          <strong className="help-note__label">{NOTE_LABEL[block.tone]}</strong>{" "}
          <Inline text={block.text} />
        </aside>
      );
  }
}

/** `**Label**` is a control as it appears on screen; the rest is plain text. */
function Inline({ text }: { readonly text: string }): ReactNode {
  const parts = text.split("**");
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <strong key={index} className="help-ui">
            {part}
          </strong>
        ) : (
          part
        ),
      )}
    </>
  );
}
